import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import Navbar from '../../../../../components/navbar.jsx';
import Footer from '../../../../../components/footer.jsx';
import Button from '../../../../../components/Button.jsx';
import Pagination from '../../../../../components/Pagination.jsx';
import DatePicker from '../../../../../components/DatePicker.jsx';
import { ReportsSidebar, ReportsTable } from '../../components/index.js';
import { StatsCard } from '../../../../../components/stats-card.jsx';
import { getBranchByCode } from '../../../../../api/branchManagement]/branchApi.js';
import { doctorScriptStatsApi } from '../../../../../api/dispensary/reports/ScriptStats/DoctorScriptStats/DoctorScriptStatsApi.js';
import PrintReport from '../../components/PrintReport.jsx';
import { useAuth } from '../../../../../context/AuthContext.jsx';
import styles from '../css/ScriptStatistics.module.css';

const ScriptStatistics = () => {
  // Get user authentication data
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [branchInfo, setBranchInfo] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // State for detail view
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Max date set to today to prevent future date selection
  const maxDate = new Date();

  // Fetch branch information when component mounts
  useEffect(() => {
    const fetchBranchInfo = async () => {
      try {
        if (user?.branchcode) {
          const response = await getBranchByCode(user.branchcode);
          if (response.success) {
            setBranchInfo(response.data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch branch info:', error);
        // Set default branch info if API fails
        setBranchInfo({
          branchname: 'DURBAN NORTH PHARMACY',
          address: '123 Umhlanga Rocks Drive, Durban North, 4051',
          telephoneno: '(031) 123-4567'
        });
      }
    };

    fetchBranchInfo();
  }, [user?.branchcode]);

  // Handle URL parameter for selected card
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const cardIndex = urlParams.get('card');
    
    if (cardIndex !== null && rows[cardIndex]) {
      const card = rows[cardIndex];
      
      if (card.title === 'Consolidated') {
        // For Consolidated, set the original table data
        setSelectedCard({
          ...card,
          isOverview: true,
          originalData: originalTableData
        });
      } else {
        if (card.title.trim() === 'Doctor') {
          setSelectedCard({
            ...card,
            isOverview: false,
            details: []
          });
        } else {
          // For other cards, generate dummy data
          setSelectedCard({
            ...card,
            isOverview: false,
            details: generateDummyData(card.title)
          });
        }
      }
    } else {
      setSelectedCard(null);
    }
  }, [location.search]);

  useEffect(() => {
    const fetchDoctorDetails = async () => {
      try {
        if (!selectedCard || selectedCard.isOverview || selectedCard.title.trim() !== 'Doctor') {
          return;
        }

        setDetailLoading(true);
        setCurrentPage(1);

        const formattedFromDate = fromDate ? fromDate.toISOString().split('T')[0] : null;
        const formattedToDate = toDate ? toDate.toISOString().split('T')[0] : null;

        const response = await doctorScriptStatsApi.getDoctorScriptTotals(
          formattedFromDate,
          formattedToDate,
        );

        if (response?.success) {
          setSelectedCard((prev) => {
            if (!prev || prev.isOverview || prev.title.trim() !== 'Doctor') return prev;
            return { ...prev, details: response.data || [] };
          });
        }
      } catch (error) {
        console.error('Failed to fetch doctor script totals:', error);
      } finally {
        setDetailLoading(false);
      }
    };

    fetchDoctorDetails();
  }, [selectedCard?.title, selectedCard?.isOverview, fromDate, toDate]);

  useEffect(() => {
    const fetchDoctorTotalsForOverview = async () => {
      try {
        if (!selectedCard || !selectedCard.isOverview) return;

        setDetailLoading(true);

        const formattedFromDate = fromDate ? fromDate.toISOString().split('T')[0] : null;
        const formattedToDate = toDate ? toDate.toISOString().split('T')[0] : null;

        const response = await doctorScriptStatsApi.getDoctorScriptTotals(
          formattedFromDate,
          formattedToDate,
        );

        if (!response?.success) return;

        const totalsRow = (response.data || []).find((r) => r.scriptNumber === 'TOTALS');
        if (!totalsRow) return;

        setSelectedCard((prev) => {
          if (!prev || !prev.isOverview) return prev;
          const data = prev.originalData || [];

          const updated = data.map((row) => {
            if (row.scriptStatistics !== 'Doctor Scripts Total') return row;
            return {
              ...row,
              grossIncome: totalsRow.grossIncome,
              disc: totalsRow.discount,
              admin: totalsRow.admin,
              netIncome: totalsRow.netIncome,
              member: totalsRow.member,
              claim: totalsRow.claim,
              dispFee: totalsRow.dispFee,
              otherFees: totalsRow.otherFees,
            };
          });

          return { ...prev, originalData: updated };
        });
      } catch (error) {
        console.error('Failed to fetch doctor totals for consolidated view:', error);
      } finally {
        setDetailLoading(false);
      }
    };

    fetchDoctorTotalsForOverview();
  }, [selectedCard?.isOverview, fromDate, toDate]);

  // Pagination calculations for detail view
  const detailData = useMemo(() => {
    if (!selectedCard) return [];
    
    if (selectedCard.isOverview) {
      return selectedCard.originalData || [];
    } else {
      return selectedCard.details || [];
    }
  }, [selectedCard]);

  // Original table columns for Overview
  const originalTableColumns = [
    { header: 'Script Statistics', accessor: 'scriptStatistics' },
    { header: 'Gross Income', accessor: 'grossIncome' },
    { header: 'Disc', accessor: 'disc' },
    { header: 'Admin', accessor: 'admin' },
    { header: 'Net Income', accessor: 'netIncome' },
    { header: 'Member', accessor: 'member' },
    { header: 'Claim', accessor: 'claim' },
    { header: 'Disp Fee', accessor: 'dispFee' },
    { header: 'Other Fees', accessor: 'otherFees' }
  ];

  const detailColumnsWithDate = [
    { header: 'Script Number', accessor: 'scriptNumber' },
    { header: 'Patient Name', accessor: 'patientName' },
    { header: 'Date', accessor: 'date' },
    { header: 'Gross Income', accessor: 'grossIncome' },
    { header: 'Discount', accessor: 'discount' },
    { header: 'Admin', accessor: 'admin' },
    { header: 'Net Income', accessor: 'netIncome' },
    { header: 'Member', accessor: 'member' },
    { header: 'Claim', accessor: 'claim' },
    { header: 'Disp Fee', accessor: 'dispFee' },
    { header: 'Other Fees', accessor: 'otherFees' }
  ];

  // Detail table columns for individual script stats
  const detailColumns = [
    { header: 'Script Number', accessor: 'scriptNumber' },
    { header: 'Patient Name', accessor: 'patientName' },
    { header: 'Gross Income', accessor: 'grossIncome' },
    { header: 'Discount', accessor: 'discount' },
    { header: 'Admin', accessor: 'admin' },
    { header: 'Net Income', accessor: 'netIncome' },
    { header: 'Member', accessor: 'member' },
    { header: 'Claim', accessor: 'claim' },
    { header: 'Disp Fee', accessor: 'dispFee' },
    { header: 'Other Fees', accessor: 'otherFees' }
  ];

  const rowsPerPage = 5;
  const totalPages = Math.max(1, Math.ceil(detailData.length / rowsPerPage));
  const safeCurrentPage = Math.min(Math.max(1, currentPage), totalPages);
  const startIndex = (safeCurrentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const pageRows = detailData.slice(startIndex, endIndex);

  // Generate dummy data for the selected script stat
  const generateDummyData = (scriptStatName) => {
    const dummyRecords = [];
    const recordCount = Math.floor(Math.random() * 10) + 5; // 5-15 records
    
    for (let i = 1; i <= recordCount; i++) {
      dummyRecords.push({
        id: i,
        scriptNumber: `SCR${String(i).padStart(4, '0')}`,
        patientName: `Patient ${i}`,
        date: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toLocaleDateString(),
        grossIncome: (Math.random() * 1000 + 100).toFixed(2),
        discount: (Math.random() * 50).toFixed(2),
        admin: (Math.random() * 20).toFixed(2),
        netIncome: (Math.random() * 1000 + 100).toFixed(2),
        member: (Math.random() * 500 + 50).toFixed(2),
        claim: (Math.random() * 500 + 50).toFixed(2),
        dispFee: (Math.random() * 100 + 10).toFixed(2),
        otherFees: (Math.random() * 30).toFixed(2),
        disc: (Math.random() * 50).toFixed(2)
      });
    }
    
    return dummyRecords;
  };

  const handleCardClick = (index) => {
    navigate(`${location.pathname}?card=${index}`);
  };

  // Custom back handler for the global navbar
  const handleNavbarBack = () => {
    if (selectedCard) {
      // If we're in detail view, go back to cards
      navigate(-1);
    } else {
      // If we're in cards view, go to the specified back route
      navigate('/customer/dispensary', { replace: true });
    }
  };

  // Print and export handlers
  const handlePrint = () => {
    if (selectedCard) {
      if (selectedCard.isOverview) {
        PrintReport({
          reportType: 'script',
          reportTitle: 'SCRIPT STATISTICS REPORT - Consolidated',
          branchInfo,
          headers: ['Script Statistics', 'Gross Income', 'Discount', 'Admin', 'Net Income', 'Member', 'Claim', 'Disp Fee', 'Other Fees'],
          data: selectedCard.originalData,
          dateRange: { fromDate, toDate },
          isPrint: true
        });
      } else {
        PrintReport({
          reportType: 'script',
          reportTitle: `SCRIPT STATISTICS REPORT - ${selectedCard.title}`,
          branchInfo,
          headers: selectedCard.title.trim() === 'Doctor'
            ? ['Script Number', 'Patient Name', 'Gross Income', 'Discount', 'Admin', 'Net Income', 'Member', 'Claim', 'Disp Fee', 'Other Fees']
            : ['Script Number', 'Patient Name', 'Date', 'Gross Income', 'Discount', 'Admin', 'Net Income', 'Member', 'Claim', 'Disp Fee', 'Other Fees'],
          data: selectedCard.details,
          dateRange: { fromDate, toDate },
          isPrint: true
        });
      }
    }
  };

  const handleExport = () => {
    if (selectedCard) {
      if (selectedCard.isOverview) {
        PrintReport({
          reportType: 'script',
          reportTitle: 'SCRIPT STATISTICS REPORT - Consolidated',
          branchInfo,
          headers: ['Script Statistics', 'Gross Income', 'Discount', 'Admin', 'Net Income', 'Member', 'Claim', 'Disp Fee', 'Other Fees'],
          data: selectedCard.originalData,
          dateRange: { fromDate, toDate },
          isPrint: false
        });
      } else {
        PrintReport({
          reportType: 'script',
          reportTitle: `SCRIPT STATISTICS REPORT - ${selectedCard.title}`,
          branchInfo,
          headers: selectedCard.title.trim() === 'Doctor'
            ? ['Script Number', 'Patient Name', 'Gross Income', 'Discount', 'Admin', 'Net Income', 'Member', 'Claim', 'Disp Fee', 'Other Fees']
            : ['Script Number', 'Patient Name', 'Date', 'Gross Income', 'Discount', 'Admin', 'Net Income', 'Member', 'Claim', 'Disp Fee', 'Other Fees'],
          data: selectedCard.details,
          dateRange: { fromDate, toDate },
          isPrint: false
        });
      }
    }
  };

  // Original table data for Overview
  const originalTableData = [
    {
      scriptStatistics: 'Claimable Scripts',
      grossIncome: '31051.81',
      disc: '0.00',
      admin: '3.30',
      netIncome: '31051.81',
      member: '1508.02',
      claim: '29540.49',
      dispFee: '3160.21',
      otherFees: '0.00'
    },
    {
      scriptStatistics: 'Private Scripts (100% Levy)',
      grossIncome: '14929.08',
      disc: '0.00',
      admin: '0.00',
      netIncome: '14929.08',
      member: '14929.08',
      claim: '0.00',
      dispFee: '2320.64',
      otherFees: '0.00'
    },
    {
      scriptStatistics: 'Doctor Scripts Total',
      grossIncome: '45980.00',
      disc: '0.00',
      admin: '3.30',
      netIncome: '45977.59',
      member: '16437.10',
      claim: '29540.49',
      dispFee: '5480.85',
      otherFees: '0.00',
      isHighlight: true
    },
    {
      scriptStatistics: 'Direct OTC Claimable Script',
      grossIncome: '2582.59',
      disc: '0.00',
      admin: '0.00',
      netIncome: '2582.59',
      member: '117.19',
      claim: '2465.40',
      dispFee: '644.74',
      otherFees: '0.00'
    },
    {
      scriptStatistics: 'TOTALS',
      grossIncome: '94543.48',
      disc: '0.00',
      admin: '6.60',
      netIncome: '94521.07',
      member: '32991.39',
      claim: '61505.89',
      dispFee: '11606.44',
      otherFees: '0.00',
      isTotal: true
    }
  ];

  // Sample data matching the dispensary menu format
  const rows = [
    { title: 'Consolidated', icon: 'file-text' },
    { title: 'Claimable', icon: 'money-note' },
    { title: 'Private ', icon: 'lock' },
    { title: 'Doctor ', icon: 'stethoscope' },
    { title: 'Direct OTC Claimable ', icon: 'shopping-bag' },
  ];

  return (
    <div className={styles.container}>
      <Navbar onBack={handleNavbarBack} backTo="/customer/dispensary" backReplace />
      
      <div className={styles.mainContent}>
        <div className={styles.layout}>
          {/* Sidebar */}
          <div className={styles.sidebarContainer}>
            <ReportsSidebar />
          </div>
          
          {/* Content Area */}
          <div className={styles.content}>
            <div className={styles.reportContainer}>
              {!selectedCard ? (
                <>
                  {/* Header */}
                  <div className={`${styles.headerSection} ${styles.cardsHeaderSection}`}>
                    <h1 className={styles.pageTitle}>Script Statistics</h1>
                  </div>

                  {/* Cards Grid */}
                  <div className={styles.cardsGrid}>
                    {rows.map((row, index) => (
                      <StatsCard
                        key={index}
                        title={row.title}
                        icon={row.icon}
                        variant="square"
                        onClick={() => handleCardClick(index)}
                        interactive={true}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <>
                  {/* Detail View */}
                  <div className={styles.detailView}>
                    {/* Date Filters */}
                    <div className={styles.dateFiltersSection}>
                      <div className={styles.dateFilters}>
                        <div className={styles.dateFilter}>
                          <label className={styles.dateLabel}>From</label>
                          <DatePicker
                            selectedDate={fromDate}
                            onDateChange={setFromDate}
                            className={styles.dateInput}
                            maxDate={maxDate}
                            disableFutureDates={true}
                          />
                        </div>
                        
                        <div className={styles.dateFilter}>
                          <label className={styles.dateLabel}>To</label>
                          <DatePicker
                            selectedDate={toDate}
                            onDateChange={setToDate}
                            className={styles.dateInput}
                            maxDate={maxDate}
                            disableFutureDates={true}
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className={styles.actionButtonsSection}>
                      <div className={styles.actionButtons}>
                        <Button 
                          onClick={handlePrint}
                          variant="approve"
                          className={styles.printButton}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M6 9V2h12v7"/>
                            <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/>
                            <rect x="6" y="14" width="12" height="8"/>
                          </svg>
                          Print
                        </Button>
                        
                        <Button 
                          onClick={handleExport}
                          variant="primary"
                          className={styles.exportButton}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                            <polyline points="7,10 12,15 17,10"/>
                            <line x1="12" y1="15" x2="12" y2="3"/>
                          </svg>
                          Export
                        </Button>
                      </div>
                    </div>

                    {/* Detail Table */}
                    <div className={styles.tableWrapper}>
                      <ReportsTable 
                        columns={selectedCard.isOverview
                          ? originalTableColumns
                          : (selectedCard.title.trim() === 'Doctor' ? detailColumns : detailColumnsWithDate)} 
                        data={detailLoading ? [] : pageRows} 
                        striped={true}
                      />
                    </div>

                    {/* Pagination */}
                    <div className={styles.paginationContainer}>
                      <Pagination 
                        currentPage={safeCurrentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        pageWindow={{ before: 2, after: 10 }}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default ScriptStatistics;
